import type { ViewportItem } from './types/viewport-item.interface';

/**
 * ClusterDetector - Detects and groups overlapping viewport items
 *
 * This class analyzes a collection of viewport items and identifies clusters
 * where items overlap spatially. It returns a collection where:
 * - Single items (non-overlapping) pass through unchanged
 * - Clustered items are wrapped in a parent ViewportItem where data contains
 *   an array of the overlapping items
 *
 * @template TData - The type of data contained in viewport items
 */
export class ClusterDetector {
  /**
   * Detects clusters of overlapping viewport items and groups them.
   *
   * Singles (non-overlapping) pass through unchanged.
   * Clusters get wrapped in a ViewportItem where data = ViewportItem<TData>[].
   *
   * @template TData - The type of data in the items
   * @param items - Array of viewport items to analyze
   * @returns Array of items where clusters are grouped and singles pass through
   */
  detect<TData>(
    items: ViewportItem<TData>[]
  ): ViewportItem<TData | ViewportItem<TData>[]>[] {
    // Handle empty array
    if (items.length === 0) {
      return [];
    }

    // Handle single item - pass through unchanged
    if (items.length === 1) {
      return items;
    }

    // Step 2: Group items by Y-index
    const yGroups = this.groupByY(items);

    const result: ViewportItem<TData | ViewportItem<TData>[]>[] = [];

    // Step 3: Process each Y-group
    for (const [yIndex, yGroupItems] of yGroups) {
      // Step 3a: Find connected components within this Y-group
      const components = this.findConnectedComponents(yGroupItems);

      // Step 3b: Process each component
      for (const componentItems of components) {
        // Single item in component - pass through unchanged
        if (componentItems.length === 1) {
          result.push(componentItems[0]);
          continue;
        }

        // Multiple items in component - create cluster
        // Step 3b-i: Bin pack items to assign z-values
        const { items: packedItems } = this.binPack(componentItems);

        // Step 3b-ii: Calculate cluster bounds
        const visibleStartX = Math.min(
          ...componentItems.map((item) => item.visibleIndexRangeX[0])
        );
        const visibleEndX = Math.max(
          ...componentItems.map((item) => item.visibleIndexRangeX[1])
        );
        const indexStartX = Math.min(
          ...componentItems.map((item) => item.indexRangeX[0])
        );
        const indexEndX = Math.max(
          ...componentItems.map((item) => item.indexRangeX[1])
        );

        // Step 3b-iii: Transform items to local coordinates
        const transformedItems = this.transformToLocalCoordinates(
          packedItems,
          visibleStartX
        );

        // Step 3b-iv: Create cluster ViewportItem
        const clusterItem: ViewportItem<ViewportItem<TData>[]> = {
          id: `cluster_y${yIndex}_x${indexStartX}-${indexEndX}`,
          indexRangeX: [indexStartX, indexEndX],
          indexRangeY: [yIndex, yIndex],
          visibleIndexRangeX: [visibleStartX, visibleEndX],
          visibleIndexRangeY: [yIndex, yIndex],
          z: 0,
          data: transformedItems,
          insertOrder: 0,
        };

        result.push(clusterItem);
      }
    }

    // Step 4: Return all singles + all clusters
    return result;
  }

  /**
   * Groups viewport items by their Y-index (visibleIndexRangeY[0]).
   *
   * This is the first step in cluster detection - items in different rows
   * cannot overlap, so we can process each row independently.
   *
   * @template TData - The type of data in the items
   * @param items - Array of viewport items to group
   * @returns Map of Y-index to items at that Y-index
   */
  private groupByY<TData>(
    items: ViewportItem<TData>[]
  ): Map<number, ViewportItem<TData>[]> {
    const groups = new Map<number, ViewportItem<TData>[]>();

    for (const item of items) {
      const yIndex = item.visibleIndexRangeY[0];
      const group = groups.get(yIndex);

      if (group) {
        group.push(item);
      } else {
        groups.set(yIndex, [item]);
      }
    }

    return groups;
  }

  /**
   * Finds connected components within a group of items using sweep-line algorithm.
   *
   * Items belong to the same component if their indexRangeX intervals overlap
   * or touch at boundaries (intervals [0,5] and [5,10] are considered connected).
   *
   * Uses a sweep-line approach:
   * 1. Sort items by X-start position
   * 2. Maintain a running "group end" position
   * 3. If next item's start <= group end, add to current component
   * 4. Otherwise, start new component
   *
   * Time complexity: O(n log n) for sorting, O(n) for grouping.
   *
   * @template TData - The type of data in the items
   * @param items - Array of viewport items (should be from same Y-group)
   * @returns Array of connected components (each component is an array of items)
   */
  private findConnectedComponents<TData>(
    items: ViewportItem<TData>[]
  ): ViewportItem<TData>[][] {
    if (items.length === 0) {
      return [];
    }

    if (items.length === 1) {
      return [items];
    }

    // Sort by X-start position (stable sort preserves insertion order for equal values)
    const sorted = [...items].sort(
      (a, b) => a.indexRangeX[0] - b.indexRangeX[0]
    );

    const components: ViewportItem<TData>[][] = [];
    let currentComponent: ViewportItem<TData>[] = [sorted[0]];
    let currentGroupEnd = sorted[0].indexRangeX[1];

    for (let i = 1; i < sorted.length; i++) {
      const item = sorted[i];

      // Check if item overlaps or touches current component
      if (item.indexRangeX[0] <= currentGroupEnd) {
        // Item belongs to current component
        currentComponent.push(item);
        // Extend the group end if this item reaches further
        currentGroupEnd = Math.max(currentGroupEnd, item.indexRangeX[1]);
      } else {
        // Gap detected - finalize current component and start new one
        components.push(currentComponent);
        currentComponent = [item];
        currentGroupEnd = item.indexRangeX[1];
      }
    }

    // Don't forget the last component
    components.push(currentComponent);

    return components;
  }

  /**
   * Checks if an interval overlaps with any interval in a list.
   *
   * Two intervals [a,b] and [c,d] overlap if a <= d AND b >= c.
   *
   * @param start - Start of the interval to check
   * @param end - End of the interval to check
   * @param intervals - List of existing intervals
   * @returns true if overlap exists, false otherwise
   */
  private hasIntervalOverlap(
    start: number,
    end: number,
    intervals: [number, number][]
  ): boolean {
    for (const [existingStart, existingEnd] of intervals) {
      if (start <= existingEnd && end >= existingStart) {
        return true;
      }
    }
    return false;
  }

  /**
   * Assigns z-values to items using interval graph coloring (bin packing).
   *
   * This method ensures that no two items with overlapping X-ranges are placed
   * in the same row (z-level). Items are sorted by X-start position (ascending),
   * then by length (descending) for items with the same start position.
   *
   * Algorithm:
   * 1. Sort items by indexRangeX[0] ASC, then length DESC
   * 2. Maintain a list of occupied X-intervals per row (z-level)
   * 3. For each item, find the first row where it doesn't overlap with existing intervals
   * 4. If no existing row fits, create a new row
   *
   * Time complexity: O(n * k) where n = items, k = number of rows (typically small)
   *
   * @template TData - The type of data in the items
   * @param items - Array of viewport items to pack
   * @returns Object containing packed items with assigned z-values and maxZ
   */
  private binPack<TData>(
    items: ViewportItem<TData>[]
  ): { items: ViewportItem<TData>[]; maxZ: number } {
    if (items.length === 0) {
      return { items: [], maxZ: 0 };
    }

    // Sort by X-start position ASC, then by length DESC (longest first)
    const sorted = [...items].sort((a, b) => {
      const startDiff = a.indexRangeX[0] - b.indexRangeX[0];
      if (startDiff !== 0) {
        return startDiff;
      }
      // Same start position - longer items first
      const lengthA = a.indexRangeX[1] - a.indexRangeX[0];
      const lengthB = b.indexRangeX[1] - b.indexRangeX[0];
      return lengthB - lengthA;
    });

    // Track occupied intervals per row: rows[z] = [[start, end], ...]
    const rows: [number, number][][] = [];
    const result: ViewportItem<TData>[] = [];
    let maxZ = 0;

    for (const item of sorted) {
      const itemStart = item.indexRangeX[0];
      const itemEnd = item.indexRangeX[1];
      let assignedZ = -1;

      // Try to find an existing row where this item fits
      for (let z = 0; z < rows.length; z++) {
        if (!this.hasIntervalOverlap(itemStart, itemEnd, rows[z])) {
          // Found a row without overlap
          assignedZ = z;
          rows[z].push([itemStart, itemEnd]);
          break;
        }
      }

      // If no existing row fits, create a new row
      if (assignedZ === -1) {
        assignedZ = rows.length;
        rows.push([[itemStart, itemEnd]]);
        maxZ = assignedZ;
      }

      // Create new item with assigned z (do not mutate original)
      result.push({ ...item, z: assignedZ });
    }

    return { items: result, maxZ };
  }

  /**
   * Transforms visibleIndexRangeX of items from global to local coordinates.
   *
   * This method converts the X-coordinates of cluster member items to be relative
   * to the cluster's starting position (clusterStartX). The z-value assigned by
   * binPack() is preserved, as are all other properties.
   *
   * @template TData - The type of data in the items
   * @param items - Array of viewport items with assigned z-values
   * @param clusterStartX - The minimum visibleIndexRangeX[0] of all items in the cluster
   * @returns New array of items with transformed visibleIndexRangeX coordinates
   */
  private transformToLocalCoordinates<TData>(
    items: ViewportItem<TData>[],
    clusterStartX: number
  ): ViewportItem<TData>[] {
    return items.map((item) => ({
      ...item,
      visibleIndexRangeX: [
        item.visibleIndexRangeX[0] - clusterStartX,
        item.visibleIndexRangeX[1] - clusterStartX,
      ] as [number, number],
    }));
  }
}
